
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface NotificationPreference {
  id: string;
  user_id: string;
  notification_type: string;
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  frequency: 'immediate' | 'daily' | 'weekly' | 'never';
  created_at: string;
  updated_at: string;
}

export const useNotificationPreferences = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch user notification preferences
  const {
    data: preferences = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('notification_user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .order('notification_type');
      
      if (error) throw error;
      return data as NotificationPreference[];
    },
  });

  // Create default preferences mutation
  const createDefaultPreferencesMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const defaultTypes = [
        'order_created',
        'order_updated',
        'product_low_stock',
        'product_out_of_stock',
        'payment_success'
      ];

      const defaultPreferences = defaultTypes.map(type => ({
        user_id: user.id,
        notification_type: type,
        email_enabled: true,
        sms_enabled: false,
        push_enabled: true,
        frequency: 'immediate' as const
      }));

      const { error } = await supabase
        .from('notification_user_preferences')
        .insert(defaultPreferences);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to create default preferences',
        variant: 'destructive',
      });
    },
  });

  // Update preference mutation
  const updatePreferenceMutation = useMutation({
    mutationFn: async ({
      notificationType,
      updates
    }: {
      notificationType: string;
      updates: Partial<NotificationPreference>;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('notification_user_preferences')
        .upsert({
          user_id: user.id,
          notification_type: notificationType,
          ...updates
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast({
        title: 'Success',
        description: 'Notification preferences updated',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to update preferences',
        variant: 'destructive',
      });
    },
  });

  // Helper function to check if notification is enabled
  const isNotificationEnabled = (type: string) => {
    const preference = preferences.find(p => p.notification_type === type);
    return preference?.push_enabled !== false;
  };

  // Helper function to check if email is enabled
  const isEmailEnabled = (type: string) => {
    const preference = preferences.find(p => p.notification_type === type);
    return preference?.email_enabled === true;
  };

  return {
    preferences,
    isLoading,
    error,
    isUpdating: updatePreferenceMutation.isPending,
    updatePreference: updatePreferenceMutation.mutate,
    createDefaultPreferences: createDefaultPreferencesMutation.mutate,
    isNotificationEnabled,
    isEmailEnabled,
  };
};
